import { useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

export const EMBEDDED_QUERY_KEY = 'embedded';
export const EMBEDDED_QUERY_VALUE = '1';

export function isEmbeddedMode(search = '') {
  if (new URLSearchParams(search).get(EMBEDDED_QUERY_KEY) === EMBEDDED_QUERY_VALUE) {
    return true;
  }

  if (typeof window !== 'undefined' && window.ReactNativeWebView) {
    return true;
  }

  return false;
}

export function withEmbeddedQuery(to, embedded) {
  if (!embedded || typeof to !== 'string' || !to) {
    return to;
  }

  if (/^[a-z][a-z\d+\-.]*:/i.test(to)) {
    return to;
  }

  const hashIndex = to.indexOf('#');
  const hash = hashIndex >= 0 ? to.slice(hashIndex) : '';
  const withoutHash = hashIndex >= 0 ? to.slice(0, hashIndex) : to;
  const queryIndex = withoutHash.indexOf('?');
  const pathname = queryIndex >= 0 ? withoutHash.slice(0, queryIndex) : withoutHash;
  const search = queryIndex >= 0 ? withoutHash.slice(queryIndex + 1) : '';
  const params = new URLSearchParams(search);

  params.set(EMBEDDED_QUERY_KEY, EMBEDDED_QUERY_VALUE);

  const nextSearch = params.toString();
  return `${pathname}${nextSearch ? `?${nextSearch}` : ''}${hash}`;
}

export function useEmbeddedMode() {
  const location = useLocation();

  return useMemo(() => isEmbeddedMode(location.search), [location.search]);
}

export function useEmbeddedNavigation() {
  const embedded = useEmbeddedMode();
  const navigate = useNavigate();

  const toEmbeddedPath = useCallback((to) => withEmbeddedQuery(to, embedded), [embedded]);
  const navigateEmbedded = useCallback(
    (to, options) => navigate(toEmbeddedPath(to), options),
    [navigate, toEmbeddedPath]
  );

  return {
    embedded,
    navigateEmbedded,
    toEmbeddedPath
  };
}
